import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getConfig } from '../runtimeConfig';
import { cachedFetch } from '../cacheManager';

const API = getConfig().REACT_APP_API;

interface ValidationRule {
  id: string;
  customer: string;
  customerName: string;
  value: string;
  email: string;
  priority?: 'low' | 'medium' | 'high' | 'critical';
  groupId?: string;
  createdAt?: string;
}

interface CustomerGroup {
  id: string;
  name: string;
  color: string;
  customers: string[];
  createdAt?: string;
}

interface CustomerContract {
  id: number;
  customer_id: string;
  customer_name: string;
  facility: string;
  storage_type: 'PP' | 'SQM' | 'KT' | 'MT' | 'CBM' | 'TEMP' | 'AMBIENT' | 'CONTAINER';
  minimum_value: number | null;
  minimum_text: string;
  rate_value: number | null;
  rate_text: string;
  rate_unit: string;
  cost_value: number | null;
  cost_text: string;
  currency: string;
  is_active: boolean;
  notes: string;
}

interface Props {
  customerStorageData?: any[];
}

type TabType = 'rules' | 'contracts' | 'groups' | 'bulk' | 'alerts';

const ActivityInfoSettings: React.FC<Props> = ({ customerStorageData = [] }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('rules');

  // Rules state
  const [palletRules, setPalletRules] = useState<ValidationRule[]>([]);
  const [inboundRules, setInboundRules] = useState<ValidationRule[]>([]);
  const [outboundRules, setOutboundRules] = useState<ValidationRule[]>([]);
  const [expiryRules, setExpiryRules] = useState<ValidationRule[]>([]);
  const [sqmRules, setSqmRules] = useState<ValidationRule[]>([]);
  const [capacityRules, setCapacityRules] = useState<ValidationRule[]>([]);

  // Contracts state
  const [contractData, setContractData] = useState<CustomerContract[]>([]);
  const [contractFilter, setContractFilter] = useState({ facility: '', storageType: '' });

  // Alert state
  const [alertResults, setAlertResults] = useState<any>(null);

  // Customer groups
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CustomerGroup | null>(null);
  const [groupForm, setGroupForm] = useState({ name: '', color: '#3b82f6', customers: [] as string[] });

  // Customer list
  const [customerList, setCustomerList] = useState<{ code: string; name: string }[]>([]);

  // Form states with group support
  const [palletForm, setPalletForm] = useState({ customer: '', value: '100', email: '', priority: 'high' as const, useGroup: false, groupId: '' });
  const [inboundForm, setInboundForm] = useState({ customer: '', value: '30', email: '', priority: 'high' as const, useGroup: false, groupId: '' });
  const [outboundForm, setOutboundForm] = useState({ customer: '', value: '30', email: '', priority: 'high' as const, useGroup: false, groupId: '' });
  const [expiryForm, setExpiryForm] = useState({ customer: '', value: '90', email: '', priority: 'high' as const, useGroup: false, groupId: '' });
  const [sqmForm, setSqmForm] = useState({ customer: '', value: '500', email: '', priority: 'high' as const, useGroup: false, groupId: '' });
  const [capacityForm, setCapacityForm] = useState({ customer: '', value: '75', email: '', priority: 'high' as const, useGroup: false, groupId: '' });

  // Bulk operations
  const [bulkEmail, setBulkEmail] = useState('');
  const [bulkCustomers, setBulkCustomers] = useState<string[]>([]);
  const [bulkRuleType, setBulkRuleType] = useState<'pallet' | 'inbound' | 'outbound' | 'expiry' | 'sqm' | 'capacity'>('pallet');
  const [bulkValue, setBulkValue] = useState('100');

  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch customers
  const fetchCustomers = useCallback(async () => {
    try {
      const data = await cachedFetch(`${API}/customers/list`, { ttl: 600000 });
      if (data.success) {
        setCustomerList(data.data);
      }
    } catch (e) {
      const seen = new Set<string>();
      const list: { code: string; name: string }[] = [];
      customerStorageData.forEach((item: any) => {
        if (item.CUSTOMER && !seen.has(item.CUSTOMER)) {
          seen.add(item.CUSTOMER);
          list.push({ code: item.CUSTOMER, name: item.CUSTOMER_NAME || item.CUSTOMER });
        }
      });
      setCustomerList(list.sort((a, b) => a.code.localeCompare(b.code)));
    }
  }, [customerStorageData]);

  // Fetch rules
  const fetchRules = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await cachedFetch(`${API}/activity-rules`, { ttl: 120000 });
      if (data.success) {
        setPalletRules(data.data.pallet || []);
        setInboundRules(data.data.inbound || []);
        setOutboundRules(data.data.outbound || []);
        setExpiryRules(data.data.expiry || []);
        setSqmRules(data.data.sqm || []);
        setCapacityRules(data.data.capacity || []);
      }
    } catch (e) {
      console.error('Error fetching rules:', e);
      setMessage({ type: 'error', text: '⚠️ Could not load rules from server' });
      setTimeout(() => setMessage(null), 3000);
    }
    setIsLoading(false);
  }, []);

  // Fetch contracts
  const fetchContracts = useCallback(async () => {
    try {
      const data = await cachedFetch(`${API}/customer-contracts`, { ttl: 600000 });
      if (data.success) {
        setContractData(data.data || []);
      }
    } catch (e) {
      console.error('Error fetching contracts:', e);
    }
  }, []);

  // Fetch customer groups
  const fetchGroups = useCallback(async () => {
    try {
      const data = await cachedFetch(`${API}/customer-groups`, { ttl: 300000 });
      if (data.success) {
        setCustomerGroups(data.data || []);
      }
    } catch (e) {
      const stored = localStorage.getItem('customerGroups');
      if (stored) setCustomerGroups(JSON.parse(stored));
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchCustomers(), fetchRules(), fetchGroups(), fetchContracts()]).catch(console.error);
  }, [fetchCustomers, fetchRules, fetchGroups, fetchContracts]);

  const getCustomerName = (code: string) => {
    const found = customerList.find(c => c.code === code);
    return found ? found.name : code;
  };

  // Save group
  const saveGroup = async () => {
    if (!groupForm.name || groupForm.customers.length === 0) {
      setMessage({ type: 'error', text: '⚠️ Group name and at least one customer required' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    const newGroup: CustomerGroup = {
      id: editingGroup?.id || `grp_${Date.now()}`,
      name: groupForm.name,
      color: groupForm.color,
      customers: groupForm.customers,
      createdAt: editingGroup?.createdAt || new Date().toISOString()
    };

    try {
      await axios.post(`${API}/customer-groups`, newGroup);
    } catch (e) {
      // Continue with local save
    }

    if (editingGroup) {
      setCustomerGroups(prev => prev.map(g => g.id === editingGroup.id ? newGroup : g));
    } else {
      setCustomerGroups(prev => [...prev, newGroup]);
    }

    const updated = editingGroup
      ? customerGroups.map(g => g.id === editingGroup.id ? newGroup : g)
      : [...customerGroups, newGroup];
    localStorage.setItem('customerGroups', JSON.stringify(updated));

    setShowGroupModal(false);
    setEditingGroup(null);
    setGroupForm({ name: '', color: '#3b82f6', customers: [] });
    setMessage({ type: 'success', text: `✅ Group "${newGroup.name}" saved!` });
    setTimeout(() => setMessage(null), 3000);
  };

  // Delete group
  const deleteGroup = async (groupId: string) => {
    try {
      await axios.delete(`${API}/customer-groups/${groupId}`);
    } catch (e) {
      // Continue with local delete
    }
    const updated = customerGroups.filter(g => g.id !== groupId);
    setCustomerGroups(updated);
    localStorage.setItem('customerGroups', JSON.stringify(updated));
    setMessage({ type: 'success', text: '✅ Group deleted' });
    setTimeout(() => setMessage(null), 3000);
  };

  // Add rule (with group support)
  const addRule = async (type: 'pallet' | 'inbound' | 'outbound' | 'expiry' | 'sqm' | 'capacity') => {
    const forms = { pallet: palletForm, inbound: inboundForm, outbound: outboundForm, expiry: expiryForm, sqm: sqmForm, capacity: capacityForm };
    const form = forms[type];

    if (form.useGroup && form.groupId) {
      const group = customerGroups.find(g => g.id === form.groupId);
      if (!group || group.customers.length === 0) {
        setMessage({ type: 'error', text: '⚠️ Please select a valid group with customers' });
        setTimeout(() => setMessage(null), 3000);
        return;
      }
    } else if (!form.customer) {
      setMessage({ type: 'error', text: '⚠️ Please select a customer' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    if (!form.email) {
      setMessage({ type: 'error', text: '⚠️ Please enter an email' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setLoading(type);

    try {
      if (form.useGroup && form.groupId) {
        const group = customerGroups.find(g => g.id === form.groupId);
        const res = await axios.post(`${API}/activity-rules/${type}`, {
          customer: group?.customers.join(',') || '',
          customerName: `👥 ${group?.name || 'Group'}`,
          value: form.value,
          email: form.email,
          priority: form.priority,
          groupId: form.groupId,
          isGroup: true
        });
        if (res.data.success) {
          setMessage({ type: 'success', text: `✅ Rule added for group "${group?.name}" (${group?.customers.length} customers)` });
        }
      } else {
        const res = await axios.post(`${API}/activity-rules/${type}`, {
          customer: form.customer,
          customerName: getCustomerName(form.customer),
          value: form.value,
          email: form.email,
          priority: form.priority,
          isGroup: false
        });
        if (res.data.success) {
          setMessage({ type: 'success', text: `✅ Rule added successfully` });
        }
      }

      await fetchRules();

      const resetForm = { customer: '', value: form.value, email: '', priority: 'high' as const, useGroup: false, groupId: '' };
      switch (type) {
        case 'pallet': setPalletForm({ ...resetForm, value: '100' }); break;
        case 'inbound': setInboundForm({ ...resetForm, value: '30' }); break;
        case 'outbound': setOutboundForm({ ...resetForm, value: '30' }); break;
        case 'expiry': setExpiryForm({ ...resetForm, value: '90' }); break;
        case 'sqm': setSqmForm({ ...resetForm, value: '500' }); break;
        case 'capacity': setCapacityForm({ ...resetForm, value: '75' }); break;
      }
    } catch (e) {
      console.error('Error adding rule:', e);
      setMessage({ type: 'error', text: '⚠️ Failed to add rule' });
    }

    setLoading(null);
    setTimeout(() => setMessage(null), 3000);
  };

  // Remove rule
  const removeRule = async (type: 'pallet' | 'inbound' | 'outbound' | 'expiry' | 'sqm' | 'capacity', id: string) => {
    setLoading(`delete-${id}`);
    try {
      const res = await axios.delete(`${API}/activity-rules/${type}/${id}`);
      if (res.data.success) {
        await fetchRules();
        setMessage({ type: 'success', text: '✅ Rule deleted' });
      }
    } catch (e: any) {
      setMessage({ type: 'error', text: `❌ Error: ${e.response?.data?.error || e.message}` });
    }
    setLoading(null);
    setTimeout(() => setMessage(null), 3000);
  };

  // Bulk add rules
  const bulkAddRules = async () => {
    if (bulkCustomers.length === 0 || !bulkEmail) {
      setMessage({ type: 'error', text: '⚠️ Select customers and enter email' });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    setLoading('bulk');
    let successCount = 0;

    for (const customer of bulkCustomers) {
      try {
        const res = await axios.post(`${API}/activity-rules/${bulkRuleType}`, {
          customer,
          customerName: getCustomerName(customer),
          value: bulkValue,
          email: bulkEmail,
          priority: 'high'
        });
        if (res.data.success) successCount++;
      } catch (e) {
        console.error(`Error adding bulk rule for ${customer}:`, e);
      }
    }

    await fetchRules();
    setBulkCustomers([]);
    setBulkEmail('');
    setMessage({ type: 'success', text: `✅ ${successCount} rules added in bulk!` });
    setLoading(null);
    setTimeout(() => setMessage(null), 3000);
  };

  // Send test email
  const sendTestEmail = async (type: string, email: string, customer: string, value: string) => {
    if (!email) return;
    setLoading(`test-${type}-${email}`);
    try {
      await axios.post(`${API}/notifications/send-test`, { type, email, customer, value });
      setMessage({ type: 'success', text: `📧 Test email sent to ${email}` });
    } catch (e) {
      setMessage({ type: 'success', text: `📧 Test email would be sent to ${email}` });
    }
    setLoading(null);
    setTimeout(() => setMessage(null), 3000);
  };

  // Check alerts
  const checkAlerts = async () => {
    setLoading('check');
    try {
      const res = await axios.post(`${API}/customer-alerts/check`);
      if (res.data.success) {
        setAlertResults(res.data);
        const alertCount = res.data.alertsFound || 0;
        if (alertCount > 0) {
          setMessage({ type: 'error', text: `🚨 ${alertCount} alerts found!` });
        } else {
          setMessage({ type: 'success', text: '✅ No alerts - all rules passing!' });
        }
      }
    } catch (e) {
      setMessage({ type: 'error', text: '❌ Error checking alerts' });
    }
    setLoading(null);
    setTimeout(() => setMessage(null), 5000);
  };

  const totalRules = palletRules.length + inboundRules.length + outboundRules.length + expiryRules.length + sqmRules.length + capacityRules.length;

  const filteredCustomers = customerList.filter(c =>
    c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredContracts = contractData.filter(c => {
    let match = true;
    if (contractFilter.facility && c.facility !== contractFilter.facility) match = false;
    if (contractFilter.storageType && c.storage_type !== contractFilter.storageType) match = false;
    return match;
  });

  const priorityColors = {
    low: '#64748b',
    medium: '#f59e0b',
    high: '#ef4444',
    critical: '#dc2626'
  };

  const groupColors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

  const glassStyle: React.CSSProperties = {
    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8) 0%, rgba(30, 41, 59, 0.6) 100%)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05)'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.95)',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    color: '#1e293b',
    fontWeight: 500,
    transition: 'all 0.3s',
    outline: 'none',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '11px',
    color: '#94a3b8',
    marginBottom: '6px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    padding: '12px 24px',
    background: isActive
      ? 'linear-gradient(135deg, rgba(79, 172, 254, 0.9) 0%, rgba(79, 172, 254, 0.7) 100%)'
      : 'rgba(255,255,255,0.03)',
    border: isActive ? '1px solid rgba(79, 172, 254, 0.5)' : '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    color: isActive ? '#fff' : '#94a3b8',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.3s',
    backdropFilter: 'blur(10px)',
    position: 'relative',
    overflow: 'hidden'
  });

  const ruleTypeIconMap: Record<string, { icon: string; color: string; gradient: string }> = {
    pallet: { icon: '📦', color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b, #f97316)' },
    inbound: { icon: '📥', color: '#10b981', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
    outbound: { icon: '📤', color: '#f97316', gradient: 'linear-gradient(135deg, #f97316, #ea580c)' },
    expiry: { icon: '⏰', color: '#ef4444', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
    sqm: { icon: '📐', color: '#06b6d4', gradient: 'linear-gradient(135deg, #06b6d4, #0891b2)' },
    capacity: { icon: '📊', color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }
  };

  const contractStorageIcons: Record<string, string> = {
    'PP': '📦', 'SQM': '📐', 'KT': '🏗️', 'MT': '⚖️', 'CBM': '📦',
    'TEMP': '❄️', 'AMBIENT': '🌡️', 'CONTAINER': '🚢'
  };

  const renderRuleForm = (
    type: 'pallet' | 'inbound' | 'outbound' | 'expiry' | 'sqm' | 'capacity',
    form: any,
    setForm: any,
    color: string,
    valueLabel: string
  ) => (
    <div className="ais-rule-form">
      <div className="ais-rule-mode-toggle">
        <button
          className={`ais-mode-btn ${!form.useGroup ? 'active' : ''}`}
          onClick={() => setForm({ ...form, useGroup: false, groupId: '' })}
        >
          👤 Single Customer
        </button>
        <button
          className={`ais-mode-btn ${form.useGroup ? 'active' : ''}`}
          onClick={() => setForm({ ...form, useGroup: true, customer: '' })}
        >
          👥 Customer Group
        </button>
      </div>

      <div className="ais-rule-inputs">
        <div>
          <label className="ais-label">{form.useGroup ? 'Group' : 'Customer'}</label>
          {form.useGroup ? (
            <select
              className="ais-select"
              value={form.groupId}
              onChange={e => setForm({ ...form, groupId: e.target.value })}
            >
              <option value="">Select Group...</option>
              {customerGroups.map(g => (
                <option key={g.id} value={g.id}>
                  {g.name} ({g.customers.length})
                </option>
              ))}
            </select>
          ) : (
            <select
              className="ais-select"
              value={form.customer}
              onChange={e => setForm({ ...form, customer: e.target.value })}
            >
              <option value="">Select...</option>
              {filteredCustomers.map(c => <option key={c.code} value={c.code}>{c.code} - {c.name}</option>)}
            </select>
          )}
        </div>
        <div>
          <label className="ais-label">{valueLabel}</label>
          <input
            className="ais-input"
            type="number"
            value={form.value}
            onChange={e => setForm({ ...form, value: e.target.value })}
          />
        </div>
        <div>
          <label className="ais-label">Priority</label>
          <div className="ais-priority-buttons">
            {['low', 'medium', 'high', 'critical'].map(p => (
              <button
                key={p}
                className={`ais-priority-btn ${form.priority === p ? 'active' : ''}`}
                onClick={() => setForm({ ...form, priority: p })}
                title={p.charAt(0).toUpperCase() + p.slice(1)}
              >
                {p.charAt(0).toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="ais-label">Email</label>
          <input
            className="ais-input"
            type="email"
            value={form.email}
            onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="email@company.com"
          />
        </div>
        <button
          className="ais-btn ais-btn-primary ais-rule-add-btn"
          onClick={() => addRule(type)}
          disabled={loading === type}
          style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}
        >
          {loading === type ? '⏳' : '+'} Add
        </button>
      </div>
    </div>
  );

  const renderRulesList = (
    rules: ValidationRule[],
    type: 'pallet' | 'inbound' | 'outbound' | 'expiry' | 'sqm' | 'capacity',
    color: string,
    valueLabel: string
  ) => (
    <div className="ais-rules-list">
      {rules.length === 0 ? (
        <div className="ais-rules-empty">
          🔍 No rules configured yet for this type
        </div>
      ) : (
        <div className="ais-rules-scroll">
          {rules.map((rule) => (
            <div key={rule.id} className="ais-rule-item">
              <div className="ais-rule-item-left">
                <div className="ais-rule-avatar" style={{ background: `${color}20`, color }}>
                  {rule.customer.charAt(0).toUpperCase()}
                </div>
                <div>
                  <span className="ais-rule-customer">{rule.customer}</span>
                  {rule.customerName !== rule.customer && (
                    <span className="ais-rule-customer-name">({rule.customerName})</span>
                  )}
                </div>
              </div>
              <div className="ais-rule-item-right">
                <div className="ais-rule-value">
                  <span className="ais-rule-value-num">{rule.value}</span>
                  <span className="ais-rule-value-label">{valueLabel}</span>
                </div>
                <span className="ais-rule-priority" style={{ background: `${priorityColors[rule.priority || 'high']}20`, color: priorityColors[rule.priority || 'high'] }}>
                  {(rule.priority || 'high').toUpperCase()}
                </span>
                <span className="ais-rule-email">{rule.email}</span>
                <div className="ais-rule-actions">
                  <button className="ais-rule-btn ais-rule-test" onClick={() => sendTestEmail(type, rule.email, rule.customer, rule.value)} disabled={loading === `test-${type}-${rule.email}`} title="Send Test Email">
                    {loading === `test-${type}-${rule.email}` ? '⏳' : '📧'}
                  </button>
                  <button className="ais-rule-btn ais-rule-delete" onClick={() => removeRule(type, rule.id)} disabled={loading === `delete-${rule.id}`} title="Delete Rule">
                    {loading === `delete-${rule.id}` ? '⏳' : '🗑️'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderRuleCard = (
    type: 'pallet' | 'inbound' | 'outbound' | 'expiry' | 'sqm' | 'capacity',
    rules: ValidationRule[],
    form: any,
    setForm: any,
    color: string,
    valueLabel: string,
    description: string
  ) => {
    const typeInfo = ruleTypeIconMap[type];
    return (
      <div className="ais-card ais-rule-card">
        <div className="ais-rule-header">
          <div className="ais-rule-title">
            <span className="ais-rule-icon">{typeInfo.icon}</span>
            <div>
              <h3 className="ais-rule-name" style={{ color }}>{type.charAt(0).toUpperCase() + type.slice(1)} Validation</h3>
              <p className="ais-rule-description">{description}</p>
            </div>
          </div>
          <span className="ais-rule-count" style={{ background: `${color}20`, color }}>{rules.length} rules</span>
        </div>
        {renderRuleForm(type, form, setForm, color, valueLabel)}
        {renderRulesList(rules, type, color, valueLabel)}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="ais-loading">
        <div className="ais-loading-content">
          <div className="ais-loading-icon">⏳</div>
          <div className="ais-loading-title">Loading Activity Rules Dashboard...</div>
          <div className="ais-loading-subtitle">Preparing your notification settings</div>
        </div>
      </div>
    );
  }

  return (
    <div className="ais-container">
      {/* Header with gradient text */}
      <div className="ais-header">
        <div>
          <h2 className="ais-title">🔔 Advanced Activity Notification</h2>
          <p className="ais-subtitle">
            Configure intelligent alerts • <span className="ais-stat-accent ais-accent-primary">{totalRules} rules</span> • <span className="ais-stat-accent ais-accent-purple">{customerGroups.length} groups</span> • <span className="ais-stat-accent ais-accent-cyan">{customerList.length} customers</span> • <span className="ais-stat-accent ais-accent-green">{contractData.length} contracts</span>
          </p>
        </div>
        <div className="ais-header-actions">
          <button className="ais-btn ais-btn-primary" onClick={checkAlerts} disabled={loading === 'check'}>
            {loading === 'check' ? '⏳ Checking...' : '🔍 Check Alerts'}
          </button>
          <button className="ais-btn ais-btn-secondary" onClick={fetchRules}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="ais-stats-bar">
        <div className="ais-stat-card">
          <div className="ais-stat-icon">📋</div>
          <div className="ais-stat-label">Total Rules</div>
          <div className="ais-stat-value ais-accent-primary">{totalRules}</div>
        </div>
        <div className="ais-stat-card">
          <div className="ais-stat-icon">👥</div>
          <div className="ais-stat-label">Groups</div>
          <div className="ais-stat-value ais-accent-purple">{customerGroups.length}</div>
        </div>
        <div className="ais-stat-card">
          <div className="ais-stat-icon">🏢</div>
          <div className="ais-stat-label">Customers</div>
          <div className="ais-stat-value ais-accent-green">{customerList.length}</div>
        </div>
        <div className="ais-stat-card">
          <div className="ais-stat-icon">📊</div>
          <div className="ais-stat-label">Contracts</div>
          <div className="ais-stat-value ais-accent-cyan">{contractData.length}</div>
        </div>
      </div>

      {/* Message Toast */}
      {message && (
        <div className={`ais-toast ais-toast-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="ais-tabs">
        {['rules', 'contracts', 'groups', 'bulk', 'alerts'].map((tab) => (
          <button
            key={tab}
            className={`ais-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab as TabType)}
          >
            {tab === 'rules' && '📋 Rules'}
            {tab === 'contracts' && '📊 Customer Contracts'}
            {tab === 'groups' && '👥 Customer Groups'}
            {tab === 'bulk' && '⚡ Bulk Operations'}
            {tab === 'alerts' && '🚨 Alert Dashboard'}
          </button>
        ))}
      </div>

      {/* Tab Content: Rules */}
      {activeTab === 'rules' && (
        <div className="ais-rules-grid">
          {renderRuleCard('pallet', palletRules, palletForm, setPalletForm, '#f59e0b', 'Min Pallets', 'Alert if pallets fall below minimum')}
          {renderRuleCard('inbound', inboundRules, inboundForm, setInboundForm, '#10b981', 'Days', 'Alert if NO inbound in X days')}
          {renderRuleCard('outbound', outboundRules, outboundForm, setOutboundForm, '#f97316', 'Days', 'Alert if NO outbound in X days')}
          {renderRuleCard('expiry', expiryRules, expiryForm, setExpiryForm, '#ef4444', 'Max Days', 'Alert for items near expiry')}
          {renderRuleCard('sqm', sqmRules, sqmForm, setSqmForm, '#06b6d4', 'Min SQM', 'Alert if SQM usage below minimum')}
          {renderRuleCard('capacity', capacityRules, capacityForm, setCapacityForm, '#8b5cf6', 'Min %', 'Alert if utilization drops below X%')}
        </div>
      )}

      {/* Tab Content: Customer Contracts */}
      {activeTab === 'contracts' && (
        <div className="ais-tab-content">
          <div className="ais-tab-header">
            <h3>📊 Customer Contracts</h3>
            <div className="ais-filter-group">
              <select
                className="ais-select"
                value={contractFilter.facility}
                onChange={e => setContractFilter({ ...contractFilter, facility: e.target.value })}
              >
                <option value="">All Facilities</option>
                {Array.from(new Set(contractData.map(c => c.facility))).map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              <select
                className="ais-select"
                value={contractFilter.storageType}
                onChange={e => setContractFilter({ ...contractFilter, storageType: e.target.value })}
              >
                <option value="">All Types</option>
                {Array.from(new Set(contractData.map(c => c.storage_type))).map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {filteredContracts.length === 0 ? (
            <div className="ais-card ais-empty-state">
              <div className="ais-empty-icon">📊</div>
              <p className="ais-empty-title">No contracts found</p>
              <p className="ais-empty-text">Import contract data from your system to get started</p>
            </div>
          ) : (
            <div className="ais-contracts-grid">
              {filteredContracts.map((contract, idx) => (
                <div key={contract.id} className="ais-card">
                  <div className="ais-contract-header">
                    <div>
                      <h4 className="ais-contract-customer">{contract.customer_name}</h4>
                      <p className="ais-contract-id">{contract.customer_id}</p>
                    </div>
                    <span className={`ais-status ${contract.is_active ? 'active' : 'inactive'}`}>
                      {contract.is_active ? '✓ Active' : 'Inactive'}
                    </span>
                  </div>

                  <div className="ais-contract-type">
                    {contractStorageIcons[contract.storage_type] || '📦'} {contract.storage_type} • {contract.facility}
                  </div>

                  <div className="ais-contract-grid">
                    <div>
                      <div className="ais-contract-label">Minimum</div>
                      <div className="ais-contract-value">
                        {contract.minimum_value !== null ? contract.minimum_value : contract.minimum_text}
                      </div>
                    </div>
                    <div>
                      <div className="ais-contract-label">Rate</div>
                      <div className="ais-contract-value">
                        {contract.rate_value} {contract.rate_unit}
                      </div>
                    </div>
                  </div>

                  {contract.cost_value && (
                    <div className="ais-contract-cost">
                      <div className="ais-contract-label">Cost</div>
                      <div className="ais-contract-value">
                        {contract.cost_value} {contract.currency}
                      </div>
                    </div>
                  )}

                  {contract.notes && (
                    <p className="ais-contract-notes">📝 {contract.notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab Content: Groups */}
      {activeTab === 'groups' && (
        <div className="ais-tab-content">
          <div className="ais-tab-header">
            <h3>👥 Customer Groups</h3>
            <button className="ais-btn ais-btn-primary" onClick={() => { setEditingGroup(null); setGroupForm({ name: '', color: '#3b82f6', customers: [] }); setShowGroupModal(true); }}>
              + Create Group
            </button>
          </div>

          {customerGroups.length === 0 ? (
            <div className="ais-card ais-empty-state">
              <div className="ais-empty-icon">👥</div>
              <p className="ais-empty-title">No customer groups created yet</p>
              <p className="ais-empty-text">Create groups to apply rules to multiple customers at once</p>
            </div>
          ) : (
            <div className="ais-groups-grid">
              {customerGroups.map((group) => (
                <div key={group.id} className="ais-card">
                  <div className="ais-group-header">
                    <div className="ais-group-title">
                      <div className="ais-group-color" style={{ background: group.color }}></div>
                      <h4 style={{ color: group.color }}>{group.name}</h4>
                    </div>
                    <span className="ais-group-count">{group.customers.length}</span>
                  </div>
                  <div className="ais-group-customers">
                    {group.customers.map(c => (
                      <span key={c} className="ais-group-customer-chip">{c}</span>
                    ))}
                  </div>
                  <div className="ais-group-actions">
                    <button className="ais-btn ais-btn-secondary" onClick={() => { setEditingGroup(group); setGroupForm({ name: group.name, color: group.color, customers: group.customers }); setShowGroupModal(true); }}>
                      ✏️ Edit
                    </button>
                    <button className="ais-btn ais-btn-danger" onClick={() => deleteGroup(group.id)}>
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Group Modal */}
          {showGroupModal && (
            <div className="ais-modal-overlay">
              <div className="ais-modal">
                <h3 className="ais-modal-title">
                  {editingGroup ? '✏️ Edit Group' : '➕ Create Customer Group'}
                </h3>

                <div className="ais-form-group">
                  <label className="ais-label">Group Name</label>
                  <input className="ais-input" type="text" value={groupForm.name} onChange={e => setGroupForm({ ...groupForm, name: e.target.value })} placeholder="e.g., Premium Clients" />
                </div>

                <div className="ais-form-group">
                  <label className="ais-label">Color</label>
                  <div className="ais-color-picker">
                    {groupColors.map(c => (
                      <button key={c} onClick={() => setGroupForm({ ...groupForm, color: c })} className={`ais-color-swatch ${groupForm.color === c ? 'active' : ''}`} style={{ background: c }}></button>
                    ))}
                  </div>
                </div>

                <div className="ais-form-group">
                  <label className="ais-label">Search Customers</label>
                  <input className="ais-input" type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by code or name..." />
                </div>

                <div className="ais-form-group">
                  <label className="ais-label">Select Customers ({groupForm.customers.length} selected)</label>
                  <div className="ais-customer-list">
                    {filteredCustomers.map(c => (
                      <label key={c.code} className={`ais-customer-checkbox ${groupForm.customers.includes(c.code) ? 'checked' : ''}`}>
                        <input type="checkbox" checked={groupForm.customers.includes(c.code)} onChange={e => { if (e.target.checked) { setGroupForm({ ...groupForm, customers: [...groupForm.customers, c.code] }); } else { setGroupForm({ ...groupForm, customers: groupForm.customers.filter(x => x !== c.code) }); } }} />
                        <span className="ais-customer-code">{c.code}</span>
                        <span className="ais-customer-name">{c.name}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="ais-modal-actions">
                  <button className="ais-btn ais-btn-secondary" onClick={() => { setShowGroupModal(false); setEditingGroup(null); setSearchTerm(''); }}>
                    Cancel
                  </button>
                  <button className="ais-btn ais-btn-primary" onClick={saveGroup}>
                    {editingGroup ? 'Update Group' : 'Create Group'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab Content: Bulk Operations */}
      {activeTab === 'bulk' && (
        <div className="ais-tab-content">
          <div className="ais-card">
            <h3 className="ais-bulk-title">
              ⚡ Bulk Add Rules
              <span className="ais-bulk-subtitle">Apply the same rule to multiple customers</span>
            </h3>
            <p className="ais-bulk-description">Batch process rules efficiently across your customer base</p>

            <div className="ais-bulk-form">
              <div>
                <label className="ais-label">Rule Type</label>
                <select className="ais-select" value={bulkRuleType} onChange={e => setBulkRuleType(e.target.value as any)}>
                  <option value="pallet">Pallet Count</option>
                  <option value="inbound">Inbound Activity</option>
                  <option value="outbound">Outbound Activity</option>
                  <option value="expiry">Expiry</option>
                  <option value="sqm">SQM Usage</option>
                  <option value="capacity">Capacity %</option>
                </select>
              </div>
              <div>
                <label className="ais-label">Value</label>
                <input className="ais-input" type="number" value={bulkValue} onChange={e => setBulkValue(e.target.value)} />
              </div>
              <div></div>
              <div>
                <label className="ais-label">Email</label>
                <input className="ais-input" type="email" value={bulkEmail} onChange={e => setBulkEmail(e.target.value)} placeholder="notification@company.com" />
              </div>
            </div>

            <div className="ais-form-group">
              <label className="ais-label">Select Customers ({bulkCustomers.length} selected)</label>
              <div className="ais-bulk-buttons">
                <button className="ais-btn ais-btn-secondary" onClick={() => setBulkCustomers(customerList.map(c => c.code))}>✓ Select All</button>
                <button className="ais-btn ais-btn-secondary" onClick={() => setBulkCustomers([])}>✕ Clear All</button>
              </div>
              <div className="ais-bulk-customers">
                {customerList.map(c => (
                  <label key={c.code} className={`ais-bulk-customer-item ${bulkCustomers.includes(c.code) ? 'selected' : ''}`}>
                    <input type="checkbox" checked={bulkCustomers.includes(c.code)} onChange={e => { if (e.target.checked) { setBulkCustomers([...bulkCustomers, c.code]); } else { setBulkCustomers(bulkCustomers.filter(x => x !== c.code)); } }} />
                    {c.code}
                  </label>
                ))}
              </div>
            </div>

            <button className="ais-btn ais-btn-primary ais-bulk-submit" onClick={bulkAddRules} disabled={loading === 'bulk'}>
              {loading === 'bulk' ? '⏳ Adding...' : `⚡ Add Rules for ${bulkCustomers.length} Customers`}
            </button>
          </div>
        </div>
      )}

      {/* Tab Content: Alert Dashboard */}
      {activeTab === 'alerts' && (
        <div className="ais-tab-content">
          <div className="ais-alerts-stats">
            <div className="ais-stat-card ais-alert-stat">
              <div className="ais-stat-icon">📊</div>
              <div className="ais-stat-label">Total Alerts</div>
              <div className="ais-stat-value ais-accent-primary">{alertResults?.alertsFound || 0}</div>
            </div>
            <div className="ais-stat-card ais-alert-stat">
              <div className="ais-stat-icon">⚡</div>
              <div className="ais-stat-label">Triggered</div>
              <div className="ais-stat-value ais-accent-purple">{alertResults?.triggered || 0}</div>
            </div>
            <div className="ais-stat-card ais-alert-stat">
              <div className="ais-stat-icon">✓</div>
              <div className="ais-stat-label">OK</div>
              <div className="ais-stat-value ais-accent-green">{alertResults?.ok || 0}</div>
            </div>
            <div className="ais-stat-card ais-alert-stat">
              <div className="ais-stat-icon">🚨</div>
              <div className="ais-stat-label">Critical</div>
              <div className="ais-stat-value ais-accent-red ais-pulse">{alertResults?.critical || 0}</div>
            </div>
          </div>

          <div className="ais-card">
            <button
              className="ais-btn ais-btn-primary"
              onClick={checkAlerts}
              disabled={loading === 'check'}
            >
              {loading === 'check' ? '⏳ Checking All Alerts...' : '🔍 Check All Alerts Now'}
            </button>
          </div>

          {alertResults?.alerts && alertResults.alerts.length > 0 ? (
            <div className="ais-card">
              <h3 className="ais-alerts-title">Recent Triggered Alerts</h3>
              <div className="ais-alerts-list">
                {alertResults.alerts.map((alert: any, idx: number) => (
                  <div key={idx} className="ais-alert-item">
                    <div className="ais-alert-content">
                      <div className="ais-alert-name">
                        {alert.customer} - {alert.type}
                      </div>
                      <div className="ais-alert-time">
                        {new Date(alert.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <span className={`ais-alert-badge ais-severity-${alert.severity}`}>
                      {alert.severity.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="ais-card ais-empty-state">
              <div className="ais-empty-icon">✓</div>
              <p className="ais-empty-title">No alerts to display</p>
              <p className="ais-empty-text">Click "Check All Alerts Now" to refresh alert status</p>
            </div>
          )}
        </div>
      )}

      {/* API Status Footer */}
      <div className="ais-footer">
        <span className="ais-footer-dot"></span>
        API Connected • {totalRules} rules • {customerGroups.length} groups • {contractData.length} contracts
      </div>
    </div>
  );
};

export default ActivityInfoSettings;
